import { getBackendUrl } from '../utils/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import storage from '../utils/storage';

const BACKEND_URL = getBackendUrl() || '';

const RollbackManager = () => {
  const [selectedDate, setSelectedDate] = useState('');
  const [rollbackInfo, setRollbackInfo] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const getAuthHeaders = () => {
    const token = storage.getItem('token');
    return {
      headers: { Authorization: `Bearer ${token}` }
    };
  };

  // Set today as default date
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setSelectedDate(today);
  }, []);

  // Fetch rollback info when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchRollbackInfo();
    }
  }, [selectedDate]);

  const fetchRollbackInfo = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/rollback/info/${selectedDate}`,
        getAuthHeaders()
      );
      setRollbackInfo(response.data);
      
      // Also fetch full history
      const historyResponse = await axios.get(
        `${BACKEND_URL}/api/rollback/history/${selectedDate}`,
        getAuthHeaders()
      );
      setHistory(historyResponse.data.history || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα φόρτωσης πληροφοριών');
    } finally {
      setLoading(false);
    }
  };

  const handleRollbackToPrevious = async () => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να επαναφέρετε την προηγούμενη έκδοση;')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/rollback/to-previous/${selectedDate}`,
        {},
        getAuthHeaders()
      );
      
      setSuccess(response.data.message);
      
      // Refresh info after rollback
      setTimeout(() => {
        fetchRollbackInfo();
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα επαναφοράς');
    } finally {
      setLoading(false);
    }
  };

  const handleRollbackToVersion = async (historyTimestamp) => {
    if (!window.confirm(`Επαναφορά στην έκδοση ${new Date(historyTimestamp).toLocaleString('el-GR')};`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/rollback/to-version`,
        null,
        {
          ...getAuthHeaders(),
          params: {
            date: selectedDate,
            history_timestamp: historyTimestamp
          }
        }
      );
      
      setSuccess(response.data.message);
      
      // Refresh info after rollback
      setTimeout(() => {
        fetchRollbackInfo();
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα επαναφοράς');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString('el-GR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">
          🔄 Σύστημα Επαναφοράς (Rollback)
        </h2>
        <p className="text-gray-600">
          Επαναφέρετε το περιεχόμενο σε προηγούμενη έκδοση (διατηρούνται τα τελευταία 7 ημέρες)
        </p>
      </div>

      {/* Date Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Επιλογή Ημερομηνίας
        </label>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Status Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          ❌ {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
          ✅ {success}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Φόρτωση...</p>
        </div>
      )}

      {/* Rollback Info */}
      {!loading && rollbackInfo && (
        <div className="space-y-6">
          {/* Current Status */}
          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-xl font-semibold text-blue-900 mb-4">
              📊 Κατάσταση Περιεχομένου
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Ημερομηνία:</span>
                <span className="ml-2 font-semibold">{rollbackInfo.date}</span>
              </div>
              <div>
                <span className="text-gray-600">Τρέχουσα Έκδοση:</span>
                <span className="ml-2 font-semibold">
                  {rollbackInfo.current_version ? '✅ Υπάρχει' : '❌ Δεν υπάρχει'}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Διαθέσιμες Εκδόσεις:</span>
                <span className="ml-2 font-semibold">{rollbackInfo.history_count}</span>
              </div>
              <div>
                <span className="text-gray-600">Δυνατότητα Επαναφοράς:</span>
                <span className="ml-2 font-semibold">
                  {rollbackInfo.can_rollback ? '✅ Ναι' : '❌ Όχι'}
                </span>
              </div>
            </div>

            {/* Last Rollback Info */}
            {rollbackInfo.last_rollback && Object.keys(rollbackInfo.last_rollback).length > 0 && (
              <div className="mt-4 p-4 bg-yellow-100 rounded">
                <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Τελευταία Επαναφορά</h4>
                <p className="text-sm text-yellow-800">
                  Έγινε στις: {formatDate(rollbackInfo.last_rollback.rolled_back_at)}
                </p>
                <p className="text-sm text-yellow-800">
                  Από έκδοση: {rollbackInfo.last_rollback.rolled_back_from}
                </p>
              </div>
            )}
          </div>

          {/* Quick Rollback Button */}
          {rollbackInfo.can_rollback && (
            <div className="bg-orange-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-orange-900 mb-4">
                ⚡ Γρήγορη Επαναφορά
              </h3>
              <p className="text-gray-700 mb-4">
                Επαναφέρετε αμέσως στην τελευταία αποθηκευμένη έκδοση
              </p>
              <button
                onClick={handleRollbackToPrevious}
                disabled={loading}
                className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                🔄 Επαναφορά στην Προηγούμενη Έκδοση
              </button>
            </div>
          )}

          {/* History List */}
          {history.length > 0 && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                📜 Ιστορικό Εκδόσεων ({history.length})
              </h3>
              <div className="space-y-3">
                {history.map((item, index) => (
                  <div
                    key={index}
                    className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          Έκδοση #{history.length - index}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          Αποθηκεύτηκε: {formatDate(item.saved_to_history_at)}
                        </p>
                        <p className="text-sm text-gray-600">
                          Δημιουργήθηκε: {formatDate(item.created_at)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Τύπος: {item.history_type || 'auto_backup'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRollbackToVersion(item.saved_to_history_at)}
                        disabled={loading}
                        className="ml-4 px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Επαναφορά σε Αυτή
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No History Message */}
          {history.length === 0 && (
            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <p className="text-gray-600">
                Δεν υπάρχει διαθέσιμο ιστορικό για αυτήν την ημερομηνία
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RollbackManager;
